import type { PeakInfo } from '@mimir/common/dto/pollingDtos';
import type { FetchedPeaks, PeaksFetcherOps } from './peaksFetcherOps';
import type { PeaksDelta, SingleTrackPeaks } from '@mimir/common/dto/peaksDto';
import { invariant } from '@mimir/common/util/invariant';

export class PeaksFetcher implements PeaksFetcherOps {

  private peakCount = 0;
  private peaks: Uint8Array[];
  private initialized = false;
  private fetching = false;
  private nPeaksPerSecond: number | null = null;
  private maybeMoreContent = true;

  constructor(
    private peakInfo: PeakInfo,
    private fetchSingleTrackPeaks: (audioTrackIndex: number) => Promise<SingleTrackPeaks>,
    private fetchSingleTrackPeakBytes: (peakFileUrl: string) => Promise<Uint8Array>,
    private fetchPeaksDelta: (peakOffset: number) => Promise<PeaksDelta>,
  ) {
    this.peaks = [];
    for (let i = 0; i < this.peakInfo.nAudioChannels; i++) {
      this.peaks.push(new Uint8Array(0));
    }

    if (this.peakInfo.nAudioChannels === 0) {
      this.maybeMoreContent = false;
      this.initialized = true;
    }
  }


  private async initializePeaks() {
    let peakArrays: Uint8Array[];

    if (this.peakInfo.peakUrls !== null) {
      // If the peaks are already in S3, let's fetch them from there, since it
      // is more efficient and more cache friendly than the lambda
      peakArrays = await this.loadPeaksFromS3(this.peakInfo.nPeaksPerSecondPerChannel, this.peakInfo.peakUrls);
    } else {
      peakArrays = await this.fetchPeaksFromStart();
    }
    this.updatePeaksInternal(peakArrays);
  }


  private updatePeaksInternal(peakArrays: Uint8Array[]) {
    const currentPeakCount = this.peakCount;

    // To avoid complication, we want to have all peaks arrays
    // to grow at the same pace. However, since we fetch the peaks
    // of the audio tracks individually, it might be that not all
    // peaks arrays have exactly the same size. As a consequence, we
    // find the smallest array and use this minimum size to truncate
    // larger arrays
    const minimumSize = this.findMinimumSize(peakArrays);
    const newPeakCount = currentPeakCount + minimumSize;

    for (let i = 0; i < this.peakInfo.nAudioChannels; i++) {
      const old = this.peaks[i]!;
      const bytes = peakArrays[i]!;
      this.peaks[i] = new Uint8Array(newPeakCount);
      this.peaks[i]!.set(old, 0);
      this.peaks[i]!.set(bytes.slice(0, minimumSize), old.length);
    }

    this.peakCount = newPeakCount;
  }


  private findMinimumSize(peakArrays: Uint8Array[]): number {
    if (peakArrays.length === 0) {
      return 0;
    }

    let min = peakArrays[0]!.length;
    for (let i = 1; i < peakArrays.length; i++) {
      if (peakArrays[i]!.length < min) {
        min = peakArrays[i]!.length;
        // Since we will truncate some of the data,
        // let's note that there is more content to be fetched
        this.maybeMoreContent = true;
      }
    }

    return min;
  }


  /**
   * Fetches as much as possible of the content available from the
   * start of the media.
   */
  private async fetchPeaksFromStart(): Promise<Uint8Array[]> {
    const peakArrays: Uint8Array[] = [];
    const promises: Promise<SingleTrackPeaks>[] = [];
    for (let i = 0; i < this.peakInfo.nAudioChannels; i++) {
      promises.push(this.fetchSingleTrackPeaks(i));
    }
    const dtos = await Promise.all(promises);
    let maybeMore = false;
    for (let i = 0; i < this.peakInfo.nAudioChannels; i++) {
      if (this.nPeaksPerSecond === null) {
        this.nPeaksPerSecond = dtos[i]!.peaksPerSeconds;
      }

      // If any of the tracks may have more content, we want to
      // fetch more
      if (dtos[i]!.maybeMoreContent) {
        maybeMore = true;
      }
      peakArrays.push(dtos[i]!.peaks);
    }

    this.maybeMoreContent = maybeMore;
    return peakArrays;
  }


  private async loadPeaksFromS3(
    nPeaksPerSecondPerChannel: number,
    peaksUrls: string[],
  ): Promise<Uint8Array[]> {
    const promises: Promise<Uint8Array>[] = [];

    for (let i = 0; i < peaksUrls.length; i++) {
      promises.push(this.fetchSingleTrackPeakBytes(peaksUrls[i]!));
    }

    const peakArrays: Uint8Array[] = await Promise.all(promises);
    this.nPeaksPerSecond = nPeaksPerSecondPerChannel;
    this.maybeMoreContent = false;
    return peakArrays;
  }


  private async fetchAndProcessPeaksDelta() {
    const offset = this.peakCount;
    const delta = await this.fetchPeaksDelta(offset);

    // We are not supposed to get concurrent updates
    invariant(offset === this.peakCount, `Exoected peak offset to be still ${offset} but got ${this.peakCount}`);

    this.updatePeaksInternal(delta.peaksPerChannel);
    if (delta.truncated) {
      this.maybeMoreContent = true;
    }
  }


  private async fetchMorePeaks() {
    if (!this.initialized) {
      await this.initializePeaks();
      this.initialized = true;
    } else {
      await this.fetchAndProcessPeaksDelta();
    }
  }

  async fetchPeaks(): Promise<FetchedPeaks> {
    if (this.maybeMoreContent && !this.fetching) {
      try {
        this.fetching = true;
        await this.fetchMorePeaks();
      } finally {
        this.fetching = false;
      }
    }

    return {
      peaksPerChannel: this.peaks,
      complete: !this.maybeMoreContent,
    };
  }

}
